import { useEffect, useState } from "react";
import { fLong } from "../../lib/format";
import { deletePerson, editPerson, fetchPeople, fetchPersonShows, SHOWS_TOGETHER_PREVIEW, showsTogether } from "../../lib/peoplePage";
import type { Person } from "../../lib/types";
import { useApp } from "../appState";
import { Sheet } from "../Sheet";

export function PersonSheet({ id }: { id: number }) {
  const { open, close, version, changed } = useApp();
  const [p, setP] = useState<Person | null>(null);
  const [shows, setShows] = useState<{ id: number; date: string; venue: string; number: number }[]>([]);
  const [form, setForm] = useState({ name: "", email: "", hidden: false });
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  // Keyed to the person so opening someone else starts collapsed again.
  const [allFor, setAllFor] = useState<number | null>(null);
  const together = showsTogether(shows, allFor === id);

  useEffect(() => {
    let live = true;
    Promise.all([fetchPeople(), fetchPersonShows(id)])
      .then(([people, s]) => {
        if (!live) return;
        const me = people.find((x) => x.id === id) ?? null;
        if (!me) throw new Error("No such person.");
        setP(me);
        setShows(s);
        setForm({ name: me.name, email: me.email ?? "", hidden: me.hidden });
      })
      .catch((e: Error) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [id, version]);

  const fail = (e: Error) => setError(e.message);
  return (
    <Sheet title={p?.name ?? ""} label={p?.name ?? "Person"} onClose={close}>
      {error && <p className="error">{error}</p>}
      {!p && !error && <p className="loading">Loading…</p>}
      {p && (
        <>
          <div className="form">
            <label className="f"><span>Name</span><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
            <label className="f"><span>Email</span><input value={form.email} placeholder="name@example.com" onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
            <label className="checkrow">
              <input type="checkbox" checked={form.hidden} onChange={(e) => setForm({ ...form, hidden: e.target.checked })} /> Hide on shared links
            </label>
          </div>
          <section>
            <p className="eyebrow" style={{ marginBottom: 4 }}>Shows together</p>
            <div className="facts">
              {together.rows.map((s) => (
                <button type="button" key={s.id} onClick={() => open("show", s.id)}>
                  <span className="fk">#{s.number} · {fLong(s.date)}</span>
                  <span className="fv2">{s.venue}</span>
                </button>
              ))}
              {!shows.length && <p className="muted" style={{ margin: 0 }}>No shows tagged yet.</p>}
            </div>
            {together.more > 0 && (
              <button type="button" className="link" onClick={() => setAllFor(id)}>Show all {shows.length}</button>
            )}
            {allFor === id && shows.length > SHOWS_TOGETHER_PREVIEW && (
              <button type="button" className="link" onClick={() => setAllFor(null)}>Show fewer</button>
            )}
          </section>
          <div className="sheet-acts">
            <button type="button" className="btn" disabled={!form.name.trim()} onClick={() => editPerson(p.id, form.name, form.email, form.hidden).then(() => changed("Saved"), fail)}>Save</button>
            {!confirm && <button type="button" className="btn danger" onClick={() => setConfirm(true)}>Delete</button>}
          </div>
          {confirm && (
            <div className="confirm">
              <span>Delete {p.name}? Their tags come off {shows.length} shows; the shows keep their other tags.</span>
              <div className="sheet-acts">
                <button type="button" className="btn danger" onClick={() => deletePerson(p.id).then(() => { changed("Deleted"); close(); }, fail)}>Delete forever</button>
                <button type="button" className="btn ghost sm" onClick={() => setConfirm(false)}>Keep</button>
              </div>
            </div>
          )}
        </>
      )}
    </Sheet>
  );
}
